import axios from './axiosInstance';
import { MessageResponse } from '../types/messageTypes';
import { ApiResponse } from '../types/apiTypes';
import { Recipient } from '../types/userTypes';
import { compressImageFile } from '../utils/imageCompressionHelper';

export interface Signature {
  _id?: string;
  userId: string;
  html: string;
  updatedAt?: string;
}

export interface MessageFilters {
  page?: number;
  limit?: number;
}

export interface SendMessagePayload {
  recipientIds: string[]; // ID of the recipient
  subject: string; // Subject of the message
  body: string; // Body of the message
  attachments?: { name: string; url: string; mimetype: string }[];
}

export const fetchInboxMessages = async (filters?: MessageFilters): Promise<MessageResponse> => {
  const response = await axios.get<MessageResponse>('/messages/inbox', {
    params: filters, // Use filters for query parameters
  });
  return response.data;
};

export const fetchSentMessages = async (filters?: MessageFilters): Promise<MessageResponse> => {
  const response = await axios.get<MessageResponse>('/messages/sent', {
    params: filters, // Use filters for query parameters
  });
  return response.data;
};

export const sendMessage = async (payload: SendMessagePayload): Promise<void> => {
  const response = await axios.post('/messages', payload); // POST request to /messages
  return response.data;
};

export const fetchRecipients = async ({query, page = 1, limit = 20,}: {
  query?: string;
  page?: number;
  limit?: number;
}): Promise<ApiResponse<Recipient[]>> => {
  const response = await axios.get<ApiResponse<Recipient[]>>('/users/recipients', {
    params: { query, page, limit },
  });
  return response.data;
};

// Signature API
export const fetchSignature = async (): Promise<Signature> => {
  const res = await axios.get('/signature');
  return res.data.data;
};

export const saveSignature = async (html: string): Promise<Signature> => {
  const res = await axios.post('/signature', { html });
  return res.data.data;
};

export const uploadMessageAttachment = async (file: File): Promise<{ name: string; url: string; mimetype: string }> => {
  const formData = new FormData();
  formData.append('attachment', file);
  const res = await axios.post('/messages/attachments/upload', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return res.data.data;
};

export const uploadSignatureImage = async (file: File): Promise<string> => {
  // Compress/resize and convert to webp before upload
  const compressed = await compressImageFile(file, {
    maxWidthOrHeight: 600,
    maxSizeMB: 0.3,
    useWebWorker: true,
    fileType: 'image/webp',
    initialQuality: 0.7,
  });
  // Always use .webp extension for best perf
  const newFile = new File([compressed], file.name.replace(/\.[^.]+$/, '.webp'), { type: 'image/webp' });
  const formData = new FormData();
  formData.append('image', newFile);
  const res = await axios.post('/signature/upload', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return res.data.data.url;
};

export const fetchAllMessages = async (filters?: MessageFilters): Promise<MessageResponse> => {
  const response = await axios.get<MessageResponse>('/messages/all', {
    params: filters,
  });
  return response.data;
};
